import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import clinicService from '../../../../../services/clinic.service';
import { toast } from 'react-toastify';
import {
    getReminderEntries,
    getReminderIconClass,
    getReminderIconTone,
    getReminderLabel,
} from './clinicReminderUtils';
function ClinicReminderSettingsForm({ reminderSettings, updateSettings, onCancel }) {
    const { register, handleSubmit } = useForm({
        defaultValues: {
            ...reminderSettings,
            apptSummary: reminderSettings.apptSummary || 'ECP',
            callPreference: reminderSettings.callPreference || 'HOME_PHONE',
        },
    });

    const [isLoading, setIsLoading] = useState(false);
    const reminderGroups = getReminderEntries(reminderSettings);

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            const formattedData = {
                id: reminderSettings.id,
                clinicId: reminderSettings.clinicId,
                apptReminder: data.apptReminder,
                reminderTime: data.reminderTime,
                sameDayReminderTime: data.sameDayReminderTime,
                isPrebookedReminder: data.isPrebookedReminder,
                isFirstReminder: data.isFirstReminder,
                isSecondReminder: data.isSecondReminder,
                isLastReminder: data.isLastReminder,
                isSameDayReminder: data.isSameDayReminder,
                daysPrebookReminder: parseInt(data.daysPrebookReminder, 10),
                daysFirstReminder: parseInt(data.daysFirstReminder, 10),
                daysSecondReminder: parseInt(data.daysSecondReminder, 10),
                minutesToAppt: parseInt(data.minutesToAppt, 10),
                isClReplacement: data.isClReplacement,
                isClReorder: data.isClReorder,
                isBirthday: data.isBirthday,
                isThankyou: data.isThankyou,
                isWelcome: data.isWelcome,
                isEmail: data.isEmail,
                isSms: data.isSms,
                isCall: data.isCall,
                isApptSummary: data.isApptSummary,
                isClGlSummary: data.isClGlSummary,
                apptSummary: data.apptSummary,
                callPreference: data.callPreference,
                isGlPickup: data.isGlPickup,
                isClPickup: data.isClPickup,
                isFollowup: data.isFollowup,
            };

            const response = await clinicService.updateReminderSettings(reminderSettings.clinicId, formattedData);

            toast.success('Reminder settings updated', { position: toast.POSITION.BOTTOM_RIGHT });
            updateSettings?.(response.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error updating reminder settings', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-3">
                <div className="col-lg-8 clinic-reminder-panel">
                    <div className="mb-3">
                        <h5 className="clinic-reminder__title mb-1">
                            <i className="bi bi-bell text-primary me-1"></i>
                            Clinic Reminder Settings
                        </h5>
                        <p className="text-muted small mb-0">Enable or disable reminder types and delivery channels for this clinic.</p>
                    </div>

                    <div className="notifications-panel">
                        <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                            <span className="d-flex align-items-center gap-2">
                                <i className="bi bi-sliders"></i>
                                Reminder Toggles
                            </span>
                            {onCancel && (
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={onCancel} disabled={isLoading}>
                                    <i className="bi bi-x-lg me-1"></i>
                                    Cancel
                                </button>
                            )}
                        </div>
                        <div className="notifications-panel__body">
                            {reminderGroups.map((group, index) => (
                                <div key={group.title}>
                                    <div className={`clinic-reminder-section-title${index === 0 ? ' clinic-reminder-section-title--first' : ''}`}>
                                        {group.title}
                                    </div>
                                    {group.items.map((item) => (
                                        <div key={item.key} className="clinic-reminder-row">
                                            <span className="clinic-reminder-row__label">
                                                <span className={`clinic-reminder-row__icon clinic-reminder-row__icon--${getReminderIconTone(item.key)}`}>
                                                    <i className={`bi ${getReminderIconClass(item.key)}`}></i>
                                                </span>
                                                {getReminderLabel(item.key)}
                                            </span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={`reminder-${item.key}`}
                                                    disabled={isLoading}
                                                    {...register(item.key)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 clinic-reminder-preferences-panel">
                    <div className="mb-3">
                        <h5 className="clinic-reminder__title mb-1">
                            <i className="bi bi-clock-history text-primary me-1"></i>
                            Reminder Preferences
                        </h5>
                        <p className="text-muted small mb-0">Timing, call routing, and summary delivery preferences.</p>
                    </div>

                    <div className="notifications-panel">
                        <div className="notifications-panel__header">
                            <span className="d-flex align-items-center gap-2">
                                <i className="bi bi-sliders"></i>
                                Preferences
                            </span>
                        </div>
                        <div className="notifications-panel__body">
                            <div className="mb-3">
                                <label className="notifications-form-label">Call Preference</label>
                                <select className="form-select form-select-sm" disabled={isLoading} {...register('callPreference')}>
                                    <option value="DISABLED">Disabled</option>
                                    <option value="HOME_PHONE">Home Phone</option>
                                    <option value="CELL_PHONE">Cell Phone</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Appointment Summary</label>
                                <select className="form-select form-select-sm" disabled={isLoading} {...register('apptSummary')}>
                                    <option value="CLINIC">Clinic</option>
                                    <option value="ECP">ECP</option>
                                    <option value="BOTH">Both</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Reminder Time</label>
                                <input type="time" className="form-control form-control-sm" step="1" disabled={isLoading} {...register('reminderTime')} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Same Day Reminder Time</label>
                                <input type="time" className="form-control form-control-sm" step="1" disabled={isLoading} {...register('sameDayReminderTime')} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Days to Send Prebook Reminder</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('daysPrebookReminder', { valueAsNumber: true })} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Days to Send First Reminder</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('daysFirstReminder', { valueAsNumber: true })} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Days to Send Second Reminder</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('daysSecondReminder', { valueAsNumber: true })} />
                            </div>
                            <div className="mb-0">
                                <label className="notifications-form-label">Minutes Earlier</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('minutesToAppt', { valueAsNumber: true })} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="clinic-reminder-form-actions">
                {onCancel && (
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={isLoading}>
                        Cancel
                    </button>
                )}
                <button type="submit" className="btn btn-success btn-sm" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Saving…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-floppy me-1"></i>
                            Save Changes
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}

export default ClinicReminderSettingsForm;
